import { createFileRoute } from "@tanstack/react-router";
import {
  useTransactions,
  useCategories,
  cancelDeleteTransaction,
  CATEGORIES,
  Category,
} from "@/lib/store";
import { useAuthCtx } from "@/lib/auth-context";
import { TransactionList } from "@/components/TransactionList";
import { TransacoesSkeleton } from "@/components/skeletons";
import { ImportDialog } from "@/components/transactions/ImportDialog";
import { ExportMenu } from "@/components/transactions/ExportMenu";
import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/transacoes")({
  head: () => ({
    meta: [
      { title: "Transações — Seu Bolso Tranquilo" },
      { name: "description", content: "Filtre e analise todo o histórico das suas receitas e despesas em um só lugar." },
      { property: "og:title", content: "Transações — Seu Bolso Tranquilo" },
      { property: "og:description", content: "Histórico completo das suas transações com filtros por categoria e por mês." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: TransacoesPage,
  pendingComponent: TransacoesSkeleton,
});

function TransacoesPage() {
  const { user } = useAuthCtx();
  const { transactions, scheduleDeleteTransaction, loading } = useTransactions(user?.id);
  const { categories: customCategories } = useCategories(user?.id);
  const [cat, setCat] = useState<Category | "all">("all");
  const [month, setMonth] = useState<string>("all");

  const handleDelete = useCallback(
    (id: string) => {
      // Schedule the delete in the global registry — survives navigation,
      // and every consumer of useTransactions hides the row immediately.
      scheduleDeleteTransaction(id, {
        delayMs: 5000,
        onCommit: (err) => {
          if (err) toast.error("Não foi possível excluir a transação.");
        },
      });

      toast("Transação excluída", {
        description: "Você pode desfazer pelos próximos 5 segundos.",
        duration: 5000,
        action: { label: "Desfazer", onClick: () => cancelDeleteTransaction(id) },
      });
    },
    [scheduleDeleteTransaction]
  );

  const allCategoryNames = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const c of [...CATEGORIES, ...customCategories.map((c) => c.name)]) {
      if (!seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    return out;
  }, [customCategories]);

  const months = useMemo(() => {
    const set = new Set<string>();
    transactions.forEach((t) => {
      const d = new Date(t.date);
      set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    });
    return Array.from(set).sort().reverse();
  }, [transactions]);

  const filtered = useMemo(() => {
    return transactions
      .filter((t) => cat === "all" || t.category === cat)
      .filter((t) => {
        if (month === "all") return true;
        const d = new Date(t.date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === month;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, cat, month]);

  if (loading) return <TransacoesSkeleton />;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header>
        <h1 className="font-display text-3xl lg:text-4xl font-bold">Transações</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Veja e filtre todo o seu histórico financeiro.
        </p>
      </header>

      <div className="flex flex-wrap gap-3">
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value as Category | "all")}
          className="px-4 py-2.5 rounded-2xl bg-input border border-border/40 text-sm focus:border-primary focus:outline-none"
        >
          <option value="all">Todas categorias</option>
          {allCategoryNames.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-4 py-2.5 rounded-2xl bg-input border border-border/40 text-sm focus:border-primary focus:outline-none"
        >
          <option value="all">Todos os meses</option>
          {months.map((m) => {
            const [y, mm] = m.split("-");
            const label = new Date(Number(y), Number(mm) - 1).toLocaleDateString("pt-BR", {
              month: "long",
              year: "numeric",
            });
            return (
              <option key={m} value={m}>
                {label}
              </option>
            );
          })}
        </select>

        <div className="ml-auto self-center text-sm text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? "transação" : "transações"}
        </div>

        <div className="flex items-center gap-2">
          <ImportDialog />
          <ExportMenu
            transactions={filtered}
            filterLabel={[
              cat !== "all" ? `Categoria: ${cat}` : null,
              month !== "all"
                ? `Mês: ${new Date(
                    Number(month.split("-")[0]),
                    Number(month.split("-")[1]) - 1
                  ).toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ") || undefined}
          />
        </div>
      </div>

      <div className="rounded-3xl bg-gradient-card border border-border/40 p-6">
        <TransactionList transactions={filtered} onDelete={handleDelete} empty="Nada com esses filtros." />
      </div>
    </div>
  );
}
